import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useRouter } from 'next/router';
import { useInquiryModal } from '@/components/utilities/InquiryModalContext';
import { useAppContext } from '@/components/utilities/AppContext';

const DEFAULTS = {
  cs: {
    title: 'Poptejte službu',
    subtitle: 'Ozveme se vám do 1–2 pracovních dnů.',
    nameLabel: 'Jméno a příjmení',
    namePlaceholder: 'Jan Novák',
    emailLabel: 'E-mailová adresa',
    emailPlaceholder: 'jan@vaseznacka.cz',
    phoneLabel: 'Telefonní číslo',
    phonePlaceholder: '+420 123 456 789',
    websiteLabel: 'Webová stránka vaší firmy / Agentura',
    websitePlaceholder: 'leadhub.co',
    noteLabel: 'Poznámka',
    notePlaceholder: 'Co pro vás můžeme udělat?',
    submitLabel: 'Odeslat poptávku',
    closeAriaLabel: 'Zavřít',
    disclaimer: 'Odesláním souhlasíte se zpracováním osobních údajů.',
    successTitle: 'Hotovo!',
    successMessage: 'Vaši poptávku jsme přijali. Ozveme se vám brzy.',
    errorMessage: 'Nepovedlo se odeslat, zkuste to prosím znovu.'
  },
  pl: {
    title: 'Zapytaj o usługę',
    subtitle: 'Odezwiemy się w ciągu 1–2 dni roboczych.',
    nameLabel: 'Imię i nazwisko',
    namePlaceholder: 'Jan Kowalski',
    emailLabel: 'Adres e-mail',
    emailPlaceholder: 'jan@twojamarka.pl',
    phoneLabel: 'Numer telefonu',
    phonePlaceholder: '+48 123 456 789',
    websiteLabel: 'Strona internetowa Twojej firmy / Agencja',
    websitePlaceholder: 'leadhub.co',
    noteLabel: 'Wiadomość',
    notePlaceholder: 'Co możemy dla Ciebie zrobić?',
    submitLabel: 'Wyślij zapytanie',
    closeAriaLabel: 'Zamknij',
    disclaimer: 'Wysyłając formularz, wyrażasz zgodę na przetwarzanie danych osobowych.',
    successTitle: 'Gotowe!',
    successMessage: 'Otrzymaliśmy Twoje zapytanie. Wkrótce się odezwiemy.',
    errorMessage: 'Nie udało się wysłać, spróbuj ponownie.'
  }
};

const EMPTY_FORM = { firstName: '', email: '', phone: '', website: '', note: '' };

export default function ServicesInquiryFormBlock(props) {
  const { locale } = useAppContext();
  const defaults = locale !== 'pl' ? DEFAULTS.cs : DEFAULTS.pl;
  const cfg = { ...defaults, ...Object.fromEntries(Object.entries(props).filter(([, v]) => v != null && v !== '')) };
  const { isOpen, serviceId, close } = useInquiryModal();
  const router = useRouter();
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [status, setStatus] = useState('idle');

  useEffect(() => {
    if (!isOpen) {
      setFormData(EMPTY_FORM);
      setStatus('idle');
      return;
    }
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => {
      if (e.key === 'Escape') close();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [isOpen, close]);

  useEffect(() => {
    if (status !== 'success') return;
    const t = setTimeout(close, 3000);
    return () => clearTimeout(t);
  }, [status, close]);

  if (!isOpen) return null;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (status === 'submitting') return;
    setStatus('submitting');
    try {
      const res = await fetch('/api/service-inquiry', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          serviceId: serviceId || null,
          pagePath: router.asPath,
          locale: locale || router.locale || 'cs'
        })
      });
      if (!res.ok) throw new Error('submit-failed');
      setStatus('success');
    } catch {
      setStatus('error');
    }
  };

  const onOverlayClick = (e) => {
    if (e.target === e.currentTarget) close();
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[9000] flex items-center justify-center bg-black/45 p-6 backdrop-blur-[4px]"
      onClick={onOverlayClick}
      role="dialog"
      aria-modal="true"
    >
      <div className="relative max-h-[90vh] w-full max-w-[480px] overflow-y-auto rounded-2xl bg-white px-10 pt-10 pb-8 shadow-[0_25px_50px_-12px_rgba(0,0,0,0.2)] max-md:px-6 max-md:pt-9 max-md:pb-7">
        <button
          type="button"
          onClick={close}
          aria-label={cfg.closeAriaLabel}
          className="absolute top-5 right-5 flex h-9 w-9 cursor-pointer items-center justify-center rounded-full border border-[#e5e5e5] bg-transparent text-[#6D6D6D] transition-colors hover:bg-[#f5f5f5] hover:text-[#1D1816]"
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          >
            <path d="M2 2l12 12M14 2L2 14" />
          </svg>
        </button>

        {status === 'success' ? (
          <div className="px-0 pt-8 pb-4 text-center">
            <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-[#E6F9F1]">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path
                  d="M5 13L9 17L19 7"
                  stroke="#07c276"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <h3 className="mb-2.5 text-2xl font-light text-[#1D1816]">{cfg.successTitle}</h3>
            <p className="text-sm text-[#6D6D6D]">{cfg.successMessage}</p>
          </div>
        ) : (
          <>
            <h2 className="mb-1.5 text-[28px] leading-[1.2] font-light text-[#1D1816]">{cfg.title}</h2>
            <p className="mb-6 text-sm leading-[1.5] text-[#6D6D6D]">{cfg.subtitle}</p>
            <form onSubmit={onSubmit} noValidate={false}>
              <Field
                id="inquiry-name"
                label={cfg.nameLabel}
                required
                placeholder={cfg.namePlaceholder}
                type="text"
                autoComplete="name"
                value={formData.firstName}
                onChange={(v) => setFormData((d) => ({ ...d, firstName: v }))}
              />
              <Field
                id="inquiry-email"
                label={cfg.emailLabel}
                required
                placeholder={cfg.emailPlaceholder}
                type="email"
                autoComplete="email"
                value={formData.email}
                onChange={(v) => setFormData((d) => ({ ...d, email: v }))}
              />
              <Field
                id="inquiry-phone"
                label={cfg.phoneLabel}
                required
                placeholder={cfg.phonePlaceholder}
                type="tel"
                autoComplete="tel"
                value={formData.phone}
                onChange={(v) => setFormData((d) => ({ ...d, phone: v }))}
              />
              <Field
                id="inquiry-website"
                label={cfg.websiteLabel}
                placeholder={cfg.websitePlaceholder}
                type="text"
                autoComplete="url"
                value={formData.website}
                onChange={(v) => setFormData((d) => ({ ...d, website: v }))}
              />
              <Field
                id="inquiry-note"
                label={cfg.noteLabel}
                placeholder={cfg.notePlaceholder}
                type="textarea"
                value={formData.note}
                onChange={(v) => setFormData((d) => ({ ...d, note: v }))}
              />
              <button
                type="submit"
                disabled={status === 'submitting'}
                className="bg-brand-400 hover:bg-brand-500 mx-auto mt-2 block cursor-pointer rounded-full px-5 py-2.5 text-sm font-normal text-white transition-colors disabled:cursor-not-allowed disabled:opacity-60"
              >
                {status === 'submitting' ? '…' : cfg.submitLabel}
              </button>
              <p className="mt-3 text-center text-xs text-[#6D6D6D]">{cfg.disclaimer}</p>
              {status === 'error' && <p className="mt-2 text-center text-xs text-red-500">{cfg.errorMessage}</p>}
            </form>
          </>
        )}
      </div>
    </div>,
    document.body
  );
}

function Field({ id, label, required, placeholder, type, autoComplete, value, onChange }) {
  const common =
    'w-full rounded-[0.625rem] border border-[#e5e5e5] bg-white px-4 py-2.5 text-sm text-[#1D1816] outline-none transition-colors placeholder:text-[#6D6D6D] focus:border-brand-500';
  return (
    <div className="mb-4 flex flex-col gap-1.5">
      {label && (
        <label htmlFor={id} className="text-sm font-semibold text-[#1D1816]">
          {label}
          {required && <span className="text-brand-400 ml-0.5">*</span>}
        </label>
      )}
      {type === 'textarea' ? (
        <textarea
          id={id}
          placeholder={placeholder}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          required={required}
          className={`${common} min-h-[90px] resize-y`}
        />
      ) : (
        <input
          id={id}
          type={type}
          placeholder={placeholder}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          required={required}
          autoComplete={autoComplete}
          className={common}
        />
      )}
    </div>
  );
}
